import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { NavigationClickEvent } from '@theme/events';

class HeaderDesktopMenu extends Component {
    requiredRefs = ['drawer', 'trigger'];

    connectedCallback() {
        super.connectedCallback();
        this.isOpen = false;
        this.activeIndex = -1;

        this.mainLinks = Array.from(this.querySelectorAll('[ref="mainLink[]"]'));
        this.submenus = Array.from(this.querySelectorAll('[ref="submenu[]"]'));
        this.banners = Array.from(this.querySelectorAll('[ref="banner[]"]'));

        this.addEventListener('click', this.#onNavLinkClick);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener('click', this.#onNavLinkClick);
    }

    /**
     * Dispatch a `navigation:use` (GTM `navigation_use`) event when a menu link is clicked.
     * @param {MouseEvent} event
     */
    #onNavLinkClick = (event) => {
        // TODO: code review, refactor
        const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
        if (!link) return;

        const submenu = link.closest('.desktop-menu-drawer__submenu');

        if (submenu) {
            const maincategory = navText(submenu.querySelector('.desktop-menu-drawer__col--title'));
            if (!maincategory) return;

            // "All {category}" view-all link → main category only
            if (link.matches('.desktop-menu-drawer__sub-link-all')) {
                this.#dispatchNavigationClick(maincategory);
                return;
            }

            // Level 3 grandchild link → main + parent + link
            if (link.matches('.desktop-menu-drawer__sub-link')) {
                const group = link.closest('.desktop-menu-drawer__sub-group');
                const subcategory = navText(group?.querySelector('.desktop-menu-drawer__sub-link-parent'));
                this.#dispatchNavigationClick(maincategory, subcategory, navText(link));
                return;
            }

            // Level 2 parent link → main + link
            if (link.matches('.desktop-menu-drawer__sub-link-parent')) {
                this.#dispatchNavigationClick(maincategory, navText(link));
            }
            return;
        }

        // Top-level link in the main column with no submenu → main category only
        if (link.matches('.desktop-menu-drawer__link')) {
            const maincategory = navText(link.querySelector('.desktop-menu-drawer__link-text')) || navText(link);
            this.#dispatchNavigationClick(maincategory);
        }
    };

    /**
     * @param {string} maincategory
     * @param {string} [subcategory]
     * @param {string} [subsubcategory]
     */
    #dispatchNavigationClick(maincategory, subcategory, subsubcategory) {
        if (!maincategory) return;
        document.dispatchEvent(new NavigationClickEvent(maincategory, subcategory, subsubcategory));
    }

    toggle() {
        this.isOpen ? this.close() : this.open();
    }

    open() {
        this.classList.add('is-open');
        this.refs.trigger.setAttribute('aria-expanded', 'true');
        this.isOpen = true;
        document.body.classList.add('overflow-hidden');

        trapFocus(this.refs.drawer);
    }

    close() {
        this.classList.remove('is-open');
        this.refs.trigger.setAttribute('aria-expanded', 'false');
        this.isOpen = false;
        document.body.classList.remove('overflow-hidden');
        removeTrapFocus();
    }

    toggleSubmenu(event) {
        if (!event) return;

        const button = event.target.closest('[data-target-index]');

        if (!button) return;

        const index = parseInt(button.dataset.targetIndex);

        if (isNaN(index) || index === this.activeIndex) return;

        this.showSubmenu(index);
    }

    showSubmenu(index) {
        if (this.activeIndex !== -1) {
            this.toggleState(this.activeIndex, false);
        }

        this.toggleState(index, true);
        this.activeIndex = index;
    }

    toggleState(index, isActive) {
        const link = this.mainLinks.find(s => s.dataset.targetIndex == index);
        const submenu = this.submenus.find(s => s.dataset.index == index);
        const banner = this.banners.find(b => b.dataset.index == index);

        if (link) {
            link.classList.toggle('is-active', isActive);
            link.setAttribute('aria-expanded', isActive.toString());
        }

        if (submenu) submenu.classList.toggle('hidden', !isActive);
        if (banner) banner.classList.toggle('hidden', !isActive);
    }
}

if (!customElements.get('header-desktop-menu')) {
    customElements.define('header-desktop-menu', HeaderDesktopMenu);
}

/**
 * Collapse whitespace in an element's text content for navigation labels.
 * @param {Element | null | undefined} el
 * @returns {string}
 */
function navText(el) {
    return (el?.textContent ?? '').replace(/\s+/g, ' ').trim();
}